import {
    createApi,
    fetchBaseQuery,
    type BaseQueryFn,
    type FetchArgs,
} from '@reduxjs/toolkit/query/react';
import { toApiFailure, type ApiFailure } from './api-error';
import {
    parseProjectileCatalog,
    type AccountCharacter,
    type AccountCharacterCreateRequest,
    type CharacterOrderRequest,
    type AccountProfile,
    type AccountSettings,
    type AccountSettingsPatchRequest,
    type BugReportReceipt,
    type BugReportSubmission,
    type VeilSaturationId,
    type ApiResourceCollection,
    type EquipmentPiece,
    type CharacterActionBarHotkeysRequest,
    type CharacterAttributesRespecRequest,
    type CharacterEssenceRingsRespecRequest,
    type CharacterItemAffixRerollRequest,
    type CharacterItemMoveRequest,
    type CharacterItemTradeRequest,
    type AuthSessionResponse,
    type CreateCrucibleRequest,
    type JoinCrucibleRequest,
    type CharacterCommission,
    type AcceptCommissionRequest,
    type CharacterSkillPatchRequest,
    type StartingEquipmentCatalog,
    type Commission,
    type CommissionId,
    type CompendiumChapter,
    type CreateCompendiumChapterRequest,
    type SetCompendiumChapterVisionsRequest,
    type UpdateCompendiumChapterRequest,
    type CrucibleInviteStatus,
    type CrucibleMember,
    type CrucibleRecord,
    type CrucibleFlavorPreview,
    type CrucibleModelManifest,
    type CrucibleRecipeSummary,
    type CrucibleTemplate,
    type Cutscene,
    type ListOpenCruciblesQuery,
    type LoreDocument,
    type LoreDocumentSummary,
    type ItemDefinition,
    type MapCatalogEntry,
    type NamedItemDefinition,
    type NpcDefinition,
    type SoundLibraryEntry,
    type OpenCrucibleSummary,
    type PendingCrucibleCutscenes,
    type JournalEntry,
    type PendingOfferCutscene,
    type PlaceCompendiumVisionRequest,
    type ProjectileCatalog,
    type PropCatalogEntry,
    type ReceivedVision,
    type Skill,
    type UpcomingCommission,
} from '@sorrem/shared';
// Directly, not through the hooks barrel: many hooks import this store, so the barrel would be a cycle.
import { addBearerToken, expireAuthSession, getStoredSessionToken } from '@sorrem-client/ui/shared/auth-session';
import { setChapterVisionsInPlace } from './compendium-visions';
import { applyOptimistic } from './optimistic';
import { resolveNextPageStart } from './paging';

/** One of an account's characters: what most character endpoints are keyed by. */
export interface CharacterArgs {
    accountId: string;
    characterId: string;
}

/** Arguments for patching a character skill preference. */
export interface PatchSkillArgs extends CharacterArgs {
    skillId: string;
    body: CharacterSkillPatchRequest;
}

export interface CharacterCommissionArgs extends CharacterArgs {
    commissionId: CommissionId;
}

export interface CharacterCutsceneArgs extends CharacterArgs {
    cutsceneId: string;
}

export interface CreateCompendiumChapterArgs extends CharacterArgs {
    body: CreateCompendiumChapterRequest;
}

export interface UpdateCompendiumChapterArgs extends CharacterArgs {
    chapterId: string;
    body: UpdateCompendiumChapterRequest;
}

export interface DeleteCompendiumChapterArgs extends CharacterArgs {
    chapterId: string;
}

export interface SetCompendiumChapterVisionsArgs extends CharacterArgs {
    chapterId: string;
    body: SetCompendiumChapterVisionsRequest;
}

export interface AddCompendiumChapterVisionArgs extends CharacterArgs {
    chapterId: string;
    visionId: string;
    body: PlaceCompendiumVisionRequest;
}

export interface RemoveCompendiumChapterVisionArgs extends CharacterArgs {
    chapterId: string;
    visionId: string;
}

/** The URL of one of an account's characters; its own content is under it. */
function characterUrl({ accountId, characterId }: CharacterArgs): string {
    return `accounts/${encodeURIComponent(accountId)}/characters/${encodeURIComponent(characterId)}`;
}

/** Arguments for raising an Essence skill one ring with skill points (#145). */
export interface RaiseEssenceSkillArgs extends CharacterArgs {
    skillId: string;
    /** The ring raised to: the skill's next. */
    ring: number;
}

/** Arguments for placing a character's skill points again, paid with banked essence (#145). */
export interface RespecEssenceSkillsArgs extends CharacterArgs, CharacterEssenceRingsRespecRequest {}

/** Arguments for trading one of a character's items for essence. */
export interface TradeCharacterItemArgs extends CharacterArgs, CharacterItemTradeRequest {}

/** Arguments for upgrading an item, or rerolling one of its affixes. */
export interface CraftCharacterItemArgs extends CharacterArgs, Partial<CharacterItemAffixRerollRequest> {
    itemId: string;
}

/** Arguments for resetting and reallocating a character's attributes (#97). */
export interface RespecCharacterAttributesArgs extends CharacterArgs, CharacterAttributesRespecRequest {}

/** Arguments for moving one of a character's items. */
export interface MoveCharacterItemArgs extends CharacterArgs, CharacterItemMoveRequest {
    itemId: string;
}

/** Arguments for the cutscenes a character still has to see around one run. */
export interface CrucibleModelManifestArgs {
    crucibleId: string;
    /** The party the manifest is for (see `getCrucibleModelManifest`); only part of the cache key. */
    memberKey: string;
}

export interface PendingCrucibleCutscenesArgs {
    crucibleId: string;
    characterId: string;
}

/** Arguments for recording that a character watched or skipped a run's cutscene. */
export interface MarkCutsceneSeenArgs {
    accountId: string;
    crucibleId: string;
    characterId: string;
    cutsceneId: string;
}

/** Where a character's seen cutscene lives: a run's or a commission offer's alike. */
function seenCutsceneUrl(accountId: string, characterId: string, cutsceneId: string): string {
    return `${characterUrl({ accountId, characterId })}/seen-cutscenes/${encodeURIComponent(cutsceneId)}`;
}

/** Arguments for recording that a character watched or skipped a commission's offer cutscene. */
export interface MarkOfferCutsceneSeenArgs extends CharacterArgs {
    cutsceneId: string;
}

/** Arguments for creating a new account character. */
export interface CreateCharacterArgs {
    accountId: string;
    body: AccountCharacterCreateRequest;
}

/** Arguments for persisting account character order changes. */
export interface ReorderCharactersArgs {
    accountId: string;
    body: CharacterOrderRequest;
}

/** Arguments for saving a character's action-bar hotkeys. */
export interface PutCharacterActionBarHotkeysArgs extends CharacterArgs {
    body: CharacterActionBarHotkeysRequest;
}

/** Arguments for deleting an account character. */
export type DeleteCharacterArgs = CharacterArgs;

/** Arguments for patching account settings. */
export interface PatchAccountSettingsArgs {
    accountId: string;
    body: AccountSettingsPatchRequest;
}

/** Arguments for joining an open crucible. */
export interface JoinCrucibleArgs extends JoinCrucibleRequest {
    crucibleId: string;
}

/** Arguments for a character leaving a crucible it joined. */
export interface LeaveCrucibleArgs {
    crucibleId: string;
    characterId: string;
}

/** Narrows the open crucibles by display name; the pages themselves come from the query. */
export type GetOpenCruciblesArgs = Pick<ListOpenCruciblesQuery, 'search'> | void;

/** Open crucibles to a page of the browse list. */
export const OPEN_CRUCIBLES_PAGE_SIZE = 20;

/** Arguments for previewing a random crucible's flavor (#225). */
export interface PreviewCrucibleFlavorArgs {
    recipeId: string;
    veilSaturation?: VeilSaturationId;
    /** Keep the same roll (only the saturation line changes), or omit for a new one. */
    flavorSeed?: string;
}

const rawBaseQuery = fetchBaseQuery({
    baseUrl: '/api',
    prepareHeaders: (headers) => {
        addBearerToken(headers);
        return headers;
    },
});

/**
 * Wraps the fetch base query so a 401 for a request that carried the stored
 * token expires the session, mirroring fetchWithAuth, and every failure is
 * an `ApiFailure`: the shared error body, typed once (#329, Q1 C).
 */
const baseQuery: BaseQueryFn<string | FetchArgs, unknown, ApiFailure> = async (
    args,
    api,
    extraOptions,
) => {
    const sentToken = getStoredSessionToken();
    const result = await rawBaseQuery(args, api, extraOptions);
    if (result.error === undefined) {
        return { data: result.data, ...(result.meta ? { meta: result.meta } : {}) };
    }
    if (result.error.status === 401 && sentToken !== null) {
        expireAuthSession();
    }
    return { error: toApiFailure(result.error), ...(result.meta ? { meta: result.meta } : {}) };
};

/**
 * RTK Query API service for Sorrem.
 *
 * Centralises all HTTP data fetching with automatic caching,
 * request de-duplication, and tag-based cache invalidation across the
 * Character Home and HUD UIs.
 */
export const sorremApi = createApi({
    reducerPath: 'sorremApi',
    baseQuery,
    tagTypes: ['Account', 'AccountSettings', 'Characters', 'CharacterSkills', 'CharacterCommissions', 'OpenCrucibles', 'CrucibleCutscenes', 'OfferCutscenes', 'Journal', 'Compendium', 'NamedItems', 'ItemDefinitions', 'ItemModels'],
    endpoints: (builder) => ({
        /** Fetch the list of available map files for crucible creation. */
        getMapCatalog: builder.query<string[], void>({
            query: () => 'maps',
            transformResponse: (response: ApiResourceCollection<MapCatalogEntry>) =>
                response.data.map((entry) => entry.mapId),
        }),

        /** Developer-only: the lore documents (`docs/lore`), in reading order. */
        getLoreCatalog: builder.query<LoreDocumentSummary[], void>({
            query: () => 'lore/documents',
            transformResponse: (response: ApiResourceCollection<LoreDocumentSummary>) => response.data,
        }),

        /** Developer-only: one lore document, rendered from its markdown by the server. */
        getLoreDocument: builder.query<LoreDocument, string>({
            query: (slug) => `lore/documents/${encodeURIComponent(slug)}`,
        }),

        /** Developer-only: every NPC definition (`npcs.json`), for the debuggers to play their moves. */
        getNpcDefinitions: builder.query<NpcDefinition[], void>({
            query: () => 'npcs',
            transformResponse: (response: ApiResourceCollection<NpcDefinition>) => response.data,
        }),

        /** How every projectile looks (#335), read through the shared reader so a bad entry is left out. */
        getProjectileCatalog: builder.query<ProjectileCatalog, void>({
            query: () => 'projectiles',
            transformResponse: (response: unknown) => parseProjectileCatalog(response).catalog,
        }),

        /** The prop catalog (game-content/data/props.json): each prop's model, size and whether it breaks (#302), for the VFX Debugger. */
        getPropCatalog: builder.query<PropCatalogEntry[], void>({
            query: () => 'props',
            transformResponse: (response: ApiResourceCollection<PropCatalogEntry>) => response.data,
        }),

        /** Developer-only: the item catalog (game-content/data/items/, #296), for the item editor. */
        getItemDefinitions: builder.query<ItemDefinition[], void>({
            query: () => 'item-definitions',
            transformResponse: (response: ApiResourceCollection<ItemDefinition>) => response.data,
            providesTags: ['ItemDefinitions'],
        }),

        /** Developer-only: creates or replaces one item definition (the Item Editor); a 400 lists its `problems`. */
        saveItemDefinition: builder.mutation<ItemDefinition, ItemDefinition>({
            query: (definition) => ({
                url: `item-definitions/${encodeURIComponent(definition.id)}`,
                method: 'PUT',
                body: definition,
            }),
            invalidatesTags: ['ItemDefinitions'],
        }),

        /** Developer-only: every item model (game-content/data/items.json), read fresh, for the Item Editor. */
        getItemModels: builder.query<EquipmentPiece[], void>({
            query: () => 'item-models',
            transformResponse: (response: ApiResourceCollection<EquipmentPiece>) => response.data,
            providesTags: ['ItemModels'],
        }),

        /** Developer-only: every attachment slot id a model entry may name (skeleton-types.json). */
        getItemModelSlots: builder.query<string[], void>({
            query: () => 'item-models/slots',
            transformResponse: (response: { data: string[] }) => response.data,
        }),

        /** Developer-only: creates or replaces one item model entry; a 400 lists its `problems`. */
        saveItemModel: builder.mutation<EquipmentPiece, EquipmentPiece>({
            query: (piece) => ({
                url: `item-models/${encodeURIComponent(piece.id)}`,
                method: 'PUT',
                body: piece,
            }),
            invalidatesTags: ['ItemModels'],
        }),

        /** Developer-only: uploads a model's .glb into its folder; answers with the file's path under assets/. */
        uploadItemModelFile: builder.mutation<{ path: string }, { id: string; file: Blob }>({
            query: ({ id, file }) => ({
                url: `item-models/${encodeURIComponent(id)}/model`,
                method: 'PUT',
                headers: { 'Content-Type': 'model/gltf-binary' },
                body: file,
            }),
            invalidatesTags: ['ItemModels'],
        }),

        /** Developer-only: uploads a model's icon PNG into its folder. */
        uploadItemIcon: builder.mutation<{ path: string }, { id: string; png: Blob }>({
            query: ({ id, png }) => ({
                url: `item-models/${encodeURIComponent(id)}/icon`,
                method: 'PUT',
                headers: { 'Content-Type': 'image/png' },
                body: png,
            }),
        }),

        /** Every named item definition (game-content/data/named-items/), for item cards, effects and the Named Item Designer. */
        getNamedItems: builder.query<NamedItemDefinition[], void>({
            query: () => 'named-items',
            transformResponse: (response: ApiResourceCollection<NamedItemDefinition>) => response.data,
            providesTags: ['NamedItems'],
        }),

        /** Developer-only: creates or replaces one named item definition; a 400 lists its `problems`. */
        saveNamedItem: builder.mutation<NamedItemDefinition, NamedItemDefinition>({
            query: (definition) => ({
                url: `named-items/${encodeURIComponent(definition.id)}`,
                method: 'PUT',
                body: definition,
            }),
            invalidatesTags: ['NamedItems'],
        }),

        /** Developer-only: every sound file the Sound Debugger can audition (the downloaded packs and the game's own). */
        getSoundLibrary: builder.query<SoundLibraryEntry[], void>({
            query: () => 'sound-library',
            transformResponse: (response: ApiResourceCollection<SoundLibraryEntry>) => response.data,
        }),

        /**
         * The open, joinable crucibles, a page at a time (optionally filtered
         * by display name). Each page starts where the last one ended, until
         * every crucible the server counts is loaded.
         */
        getOpenCrucibles: builder.infiniteQuery<ApiResourceCollection<OpenCrucibleSummary>, GetOpenCruciblesArgs, number>({
            infiniteQueryOptions: {
                initialPageParam: 0,
                getNextPageParam: (lastPage) => resolveNextPageStart(lastPage.meta),
            },
            query: ({ queryArg, pageParam }) => {
                const params = new URLSearchParams({ start: String(pageParam), limit: String(OPEN_CRUCIBLES_PAGE_SIZE) });
                if (queryArg?.search) {
                    params.set('search', queryArg.search);
                }
                return `crucibles?${params.toString()}`;
            },
            providesTags: ['OpenCrucibles'],
        }),

        /** Join an open crucible (creates a membership). */
        joinCrucible: builder.mutation<CrucibleMember, JoinCrucibleArgs>({
            query: ({ crucibleId, characterId }) => ({
                url: `crucibles/${encodeURIComponent(crucibleId)}/members`,
                method: 'POST',
                body: (characterId ? { characterId } : {}) satisfies JoinCrucibleRequest,
            }),
            invalidatesTags: ['OpenCrucibles'],
        }),

        /** Leave a crucible (removes the character's membership); a 404 means it was already gone. */
        leaveCrucible: builder.mutation<void, LeaveCrucibleArgs>({
            query: ({ crucibleId, characterId }) => ({
                url: `crucibles/${encodeURIComponent(crucibleId)}/members/${encodeURIComponent(characterId)}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['OpenCrucibles'],
        }),

        /** Fetch a single account record by canonical account ID. */
        getAccount: builder.query<AccountProfile, string>({
            query: (accountId) => `accounts/${encodeURIComponent(accountId)}`,
            providesTags: (_, __, accountId) => [{ type: 'Account', id: accountId }],
        }),

        /** Fetch an account's user settings. */
        getAccountSettings: builder.query<AccountSettings, string>({
            query: (accountId) => `accounts/${encodeURIComponent(accountId)}/settings`,
            providesTags: (_, __, accountId) => [{ type: 'AccountSettings', id: accountId }],
        }),

        /**
         * Update account settings. Applies the patch to the cached settings
         * immediately and reverts it if the server rejects the request. The
         * cache is then the truth, so it isn't fetched again: a refetch landing
         * after a newer change would put the older one back for a moment.
         */
        patchAccountSettings: builder.mutation<AccountSettings, PatchAccountSettingsArgs>({
            query: ({ accountId, body }) => ({
                url: `accounts/${encodeURIComponent(accountId)}/settings`,
                method: 'PATCH',
                body,
            }),
            async onQueryStarted({ accountId, body }, { dispatch, queryFulfilled }) {
                await applyOptimistic(
                    dispatch(
                        sorremApi.util.updateQueryData('getAccountSettings', accountId, (draft) => {
                            Object.assign(draft, body);
                        }),
                    ),
                    queryFulfilled,
                );
            },
        }),

        /** Fetch the current session's identity/authorization claims (e.g. isDeveloper). */
        getAuthSession: builder.query<AuthSessionResponse, void>({
            query: () => 'auth/session',
        }),

        /** The starting armor and weapon options, as the server equips them at character creation. */
        getStartingEquipment: builder.query<StartingEquipmentCatalog, void>({
            query: () => 'starting-equipment',
        }),

        /** Fetch all characters belonging to an account. */
        getCharacters: builder.query<AccountCharacter[], string>({
            query: (accountId) => `accounts/${encodeURIComponent(accountId)}/characters`,
            transformResponse: (response: ApiResourceCollection<AccountCharacter>) => response.data,
            providesTags: (_, __, accountId) => [{ type: 'Characters', id: accountId }],
        }),

        /** Fetch all skills for a specific character. */
        getCharacterSkills: builder.query<Skill[], CharacterArgs>({
            query: ({ accountId, characterId }) =>
                `${characterUrl({ accountId, characterId })}/skills`,
            transformResponse: (response: ApiResourceCollection<Skill>) => response.data,
            providesTags: (_, __, { characterId }) => [{ type: 'CharacterSkills', id: characterId }],
        }),

        /** Fetch the commissions a character holds (e.g. the intro commission). */
        getCharacterCommissions: builder.query<CharacterCommission[], CharacterArgs>({
            query: ({ accountId, characterId }) =>
                `${characterUrl({ accountId, characterId })}/commissions`,
            transformResponse: (response: ApiResourceCollection<CharacterCommission>) => response.data,
            providesTags: (_, __, { characterId }) => [{ type: 'CharacterCommissions', id: characterId }],
        }),

        /** Fetch what a character still needs before each commission they're working toward is offered. */
        getUpcomingCommissions: builder.query<UpcomingCommission[], CharacterArgs>({
            query: ({ accountId, characterId }) =>
                `${characterUrl({ accountId, characterId })}/upcoming-commissions`,
            transformResponse: (response: ApiResourceCollection<UpcomingCommission>) => response.data,
            providesTags: (_, __, { characterId }) => [{ type: 'CharacterCommissions', id: characterId }],
        }),

        /** Accept one of a character's commissions, creating the purpose-built Crucible it points to. */
        acceptCommission: builder.mutation<CrucibleRecord, AcceptCommissionRequest>({
            query: (body) => ({
                url: 'crucibles',
                method: 'POST',
                body,
            }),
            invalidatesTags: (_, __, { characterId }) => [
                { type: 'CharacterCommissions', id: characterId },
                { type: 'Journal', id: characterId },
            ],
        }),

        /** Reset and reallocate a character's attributes, paid with banked essence. */
        respecCharacterAttributes: builder.mutation<void, RespecCharacterAttributesArgs>({
            query: ({ accountId, characterId, attributes }) => ({
                url: `${characterUrl({ accountId, characterId })}/attributes`,
                method: 'PUT',
                body: { attributes } satisfies CharacterAttributesRespecRequest,
            }),
            invalidatesTags: (_, __, { accountId }) => [{ type: 'Characters', id: accountId }],
        }),

        /** Raise an Essence skill one ring with skill points. */
        raiseEssenceSkill: builder.mutation<void, RaiseEssenceSkillArgs>({
            query: ({ accountId, characterId, skillId, ring }) => ({
                url: `${characterUrl({ accountId, characterId })}/skills/${encodeURIComponent(skillId)}`,
                method: 'PATCH',
                body: { ring },
            }),
            // The character's skills are cached apart from it: both refetch, so its rings and points are current.
            invalidatesTags: (_, __, { accountId, characterId }) => [{ type: 'Characters', id: accountId }, { type: 'CharacterSkills', id: characterId }],
        }),

        /** Place a character's skill points again, paid with banked essence. */
        respecEssenceSkills: builder.mutation<void, RespecEssenceSkillsArgs>({
            query: ({ accountId, characterId, rings }) => ({
                url: `${characterUrl({ accountId, characterId })}/essence-rings`,
                method: 'PUT',
                body: { rings } satisfies CharacterEssenceRingsRespecRequest,
            }),
            // The character's skills are cached apart from it: both refetch, so its rings and points are current.
            invalidatesTags: (_, __, { accountId, characterId }) => [{ type: 'Characters', id: accountId }, { type: 'CharacterSkills', id: characterId }],
        }),

        /** Move one of a character's items to a carried or equipment slot, swapping with what is there. */
        moveCharacterItem: builder.mutation<void, MoveCharacterItemArgs>({
            query: ({ accountId, characterId, itemId, target }) => ({
                url: `${characterUrl({ accountId, characterId })}/items/${encodeURIComponent(itemId)}`,
                method: 'PATCH',
                body: { target } satisfies CharacterItemMoveRequest,
            }),
            invalidatesTags: (_, __, { accountId }) => [{ type: 'Characters', id: accountId }],
        }),

        /** Trade a carried or stored item to a house of exchange; its essence goes into the vessel. */
        tradeCharacterItem: builder.mutation<void, TradeCharacterItemArgs>({
            query: ({ accountId, characterId, itemId }) => ({
                url: `${characterUrl({ accountId, characterId })}/item-trades`,
                method: 'POST',
                body: { itemId } satisfies CharacterItemTradeRequest,
            }),
            invalidatesTags: (_, __, { accountId }) => [{ type: 'Characters', id: accountId }],
        }),

        /** Raise an item one upgrade level, paid from banked essence. */
        upgradeCharacterItem: builder.mutation<void, CraftCharacterItemArgs>({
            query: ({ accountId, characterId, itemId }) => ({
                url: `${characterUrl({ accountId, characterId })}/items/${encodeURIComponent(itemId)}/upgrades`,
                method: 'POST',
            }),
            invalidatesTags: (_, __, { accountId }) => [{ type: 'Characters', id: accountId }],
        }),

        /** Roll one of an item's affixes again, paid from banked essence. */
        rerollCharacterItemAffix: builder.mutation<void, CraftCharacterItemArgs>({
            query: ({ accountId, characterId, itemId, affixIndex }) => ({
                url: `${characterUrl({ accountId, characterId })}/items/${encodeURIComponent(itemId)}/affix-rerolls`,
                method: 'POST',
                body: { affixIndex } satisfies Partial<CharacterItemAffixRerollRequest>,
            }),
            invalidatesTags: (_, __, { accountId }) => [{ type: 'Characters', id: accountId }],
        }),

        /** Fetch the offer cutscenes a character still has to see before accepting a commission. */
        getPendingOfferCutscenes: builder.query<PendingOfferCutscene[], CharacterArgs>({
            query: ({ accountId, characterId }) =>
                `${characterUrl({ accountId, characterId })}/pending-cutscenes`,
            transformResponse: (response: ApiResourceCollection<PendingOfferCutscene>) => response.data,
            providesTags: (_, __, { characterId }) => [{ type: 'OfferCutscenes', id: characterId }],
        }),

        /** Record that a character watched or skipped a commission's offer cutscene. */
        markOfferCutsceneSeen: builder.mutation<void, MarkOfferCutsceneSeenArgs>({
            query: ({ accountId, characterId, cutsceneId }) => ({
                url: seenCutsceneUrl(accountId, characterId, cutsceneId),
                method: 'PUT',
            }),
            invalidatesTags: (_, __, { characterId }) => [
                { type: 'OfferCutscenes', id: characterId },
                { type: 'Journal', id: characterId },
            ],
        }),

        /** Fetch a character's Journal: watched cutscenes and commissions, oldest first. */
        getCharacterJournal: builder.query<JournalEntry[], CharacterArgs>({
            query: ({ accountId, characterId }) =>
                `${characterUrl({ accountId, characterId })}/journal`,
            transformResponse: (response: ApiResourceCollection<JournalEntry>) => response.data,
            providesTags: (_, __, { characterId }) => [{ type: 'Journal', id: characterId }],
        }),

        /** Fetch the visions a character has received, in reading order, each with its Compendium chapter and place. */
        getCharacterVisions: builder.query<ReceivedVision[], CharacterArgs>({
            query: (args) => `${characterUrl(args)}/visions`,
            transformResponse: (response: ApiResourceCollection<ReceivedVision>) => response.data,
            providesTags: (_, __, { characterId }) => [{ type: 'Compendium', id: characterId }],
        }),

        /** Fetch a character's Compendium chapters, in order. */
        getCompendiumChapters: builder.query<CompendiumChapter[], CharacterArgs>({
            query: (args) => `${characterUrl(args)}/compendium/chapters`,
            transformResponse: (response: ApiResourceCollection<CompendiumChapter>) => response.data,
            providesTags: (_, __, { characterId }) => [{ type: 'Compendium', id: characterId }],
        }),

        /** Add a Compendium chapter after the others. */
        createCompendiumChapter: builder.mutation<CompendiumChapter, CreateCompendiumChapterArgs>({
            query: ({ body, ...args }) => ({ url: `${characterUrl(args)}/compendium/chapters`, method: 'POST', body }),
            invalidatesTags: (_, __, { characterId }) => [{ type: 'Compendium', id: characterId }],
        }),

        /** Rename a Compendium chapter, move it among the others, or both. */
        updateCompendiumChapter: builder.mutation<CompendiumChapter, UpdateCompendiumChapterArgs>({
            query: ({ chapterId, body, ...args }) => ({
                url: `${characterUrl(args)}/compendium/chapters/${encodeURIComponent(chapterId)}`,
                method: 'PATCH',
                body,
            }),
            // Shown at once, so a dragged chapter doesn't snap back while the change saves.
            async onQueryStarted({ accountId, characterId, chapterId, body }, { dispatch, queryFulfilled }) {
                await applyOptimistic(
                    dispatch(
                        sorremApi.util.updateQueryData('getCompendiumChapters', { accountId, characterId }, (chapters) => {
                            const index = chapters.findIndex((chapter) => chapter.id === chapterId);
                            const [chapter] = index === -1 ? [] : chapters.splice(index, 1);
                            if (chapter === undefined) {
                                return;
                            }
                            chapters.splice(body.position ?? index, 0, { ...chapter, name: body.name ?? chapter.name });
                        }),
                    ),
                    queryFulfilled,
                );
            },
            // Patched in place above, so the cache is already right: refetch only when the change failed (#329).
            invalidatesTags: (_, error, { characterId }) => (error ? [{ type: 'Compendium', id: characterId }] : []),
        }),

        /** Remove a Compendium chapter; its visions go to the loose pages. */
        deleteCompendiumChapter: builder.mutation<void, DeleteCompendiumChapterArgs>({
            query: ({ chapterId, ...args }) => ({
                url: `${characterUrl(args)}/compendium/chapters/${encodeURIComponent(chapterId)}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_, __, { characterId }) => [{ type: 'Compendium', id: characterId }],
        }),

        /** Set a Compendium chapter's visions, in order; any it held but leaves out go to the loose pages. */
        setCompendiumChapterVisions: builder.mutation<void, SetCompendiumChapterVisionsArgs>({
            query: ({ chapterId, body, ...args }) => ({
                url: `${characterUrl(args)}/compendium/chapters/${encodeURIComponent(chapterId)}/visions`,
                method: 'PUT',
                body,
            }),
            // Shown at once, so a dragged page doesn't snap back while the change saves.
            async onQueryStarted({ accountId, characterId, chapterId, body }, { dispatch, queryFulfilled }) {
                await applyOptimistic(
                    dispatch(
                        sorremApi.util.updateQueryData('getCharacterVisions', { accountId, characterId }, (visions) => (
                            setChapterVisionsInPlace(visions, chapterId, body.visionIds)
                        )),
                    ),
                    queryFulfilled,
                );
            },
            // Patched in place above, so the cache is already right: refetch only when the change failed (#329).
            invalidatesTags: (_, error, { characterId }) => (error ? [{ type: 'Compendium', id: characterId }] : []),
        }),

        /** Add a vision to a Compendium chapter (or move it within one). */
        addCompendiumChapterVision: builder.mutation<void, AddCompendiumChapterVisionArgs>({
            query: ({ chapterId, visionId, body, ...args }) => ({
                url: `${characterUrl(args)}/compendium/chapters/${encodeURIComponent(chapterId)}/visions/${encodeURIComponent(visionId)}`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (_, __, { characterId }) => [{ type: 'Compendium', id: characterId }],
        }),

        /** Return a vision from a Compendium chapter to the loose pages. */
        removeCompendiumChapterVision: builder.mutation<void, RemoveCompendiumChapterVisionArgs>({
            query: ({ chapterId, visionId, ...args }) => ({
                url: `${characterUrl(args)}/compendium/chapters/${encodeURIComponent(chapterId)}/visions/${encodeURIComponent(visionId)}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_, __, { characterId }) => [{ type: 'Compendium', id: characterId }],
        }),

        /** Fetch one commission's content: title, issuer, background and objective. */
        getCommission: builder.query<Commission, CharacterCommissionArgs>({
            query: ({ commissionId, ...args }) => `${characterUrl(args)}/commissions/${encodeURIComponent(commissionId)}`,
        }),

        /** Fetch one cutscene's content, with media paths resolved to URLs. */
        getCutscene: builder.query<Cutscene, CharacterCutsceneArgs>({
            query: ({ cutsceneId, ...args }) => `${characterUrl(args)}/cutscenes/${encodeURIComponent(cutsceneId)}`,
        }),

        /** Fetch the cutscenes a character still has to see before and after a run. */
        getPendingCrucibleCutscenes: builder.query<PendingCrucibleCutscenes, PendingCrucibleCutscenesArgs>({
            query: ({ crucibleId, characterId }) =>
                `crucibles/${encodeURIComponent(crucibleId)}/cutscenes?characterId=${encodeURIComponent(characterId)}`,
            providesTags: (_, __, { crucibleId, characterId }) => [{ type: 'CrucibleCutscenes', id: `${crucibleId}:${characterId}` }],
        }),

        /**
         * The models the run will show, for the gathering page to load while
         * the party gathers. `memberKey` names the party it was asked for: the
         * NPCs follow the party's strength, so a new party asks again.
         */
        getCrucibleModelManifest: builder.query<CrucibleModelManifest, CrucibleModelManifestArgs>({
            query: ({ crucibleId }) => `crucibles/${encodeURIComponent(crucibleId)}/model-manifest`,
        }),

        /** Record that a character watched or skipped one of a run's cutscenes. */
        markCutsceneSeen: builder.mutation<void, MarkCutsceneSeenArgs>({
            query: ({ accountId, characterId, cutsceneId }) => ({
                url: seenCutsceneUrl(accountId, characterId, cutsceneId),
                method: 'PUT',
            }),
            invalidatesTags: (_, __, { crucibleId, characterId }) => [
                { type: 'CrucibleCutscenes', id: `${crucibleId}:${characterId}` },
                { type: 'Journal', id: characterId },
            ],
        }),

        /** Create a new character for an account. */
        createCharacter: builder.mutation<AccountCharacter, CreateCharacterArgs>({
            query: ({ accountId, body }) => ({
                url: `accounts/${encodeURIComponent(accountId)}/characters`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (_, __, { accountId }) => [
                { type: 'Characters', id: accountId },
                { type: 'Account', id: accountId },
            ],
        }),

        /** Persist the display order for an account's characters. */
        reorderCharacters: builder.mutation<CharacterOrderRequest, ReorderCharactersArgs>({
            query: ({ accountId, body }) => ({
                url: `accounts/${encodeURIComponent(accountId)}/character-order`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: (_, __, { accountId }) => [{ type: 'Characters', id: accountId }],
        }),

        /**
         * Save what each of a character's action-bar slots triggers. The
         * caller puts the change into the `getCharacters` cache first (see
         * `useActionBarTargets`); the cache is then the truth, so nothing is
         * refetched.
         */
        putCharacterActionBarHotkeys: builder.mutation<CharacterActionBarHotkeysRequest, PutCharacterActionBarHotkeysArgs>({
            query: ({ accountId, characterId, body }) => ({
                url: `${characterUrl({ accountId, characterId })}/action-bar-hotkeys`,
                method: 'PUT',
                body,
            }),
        }),

        /** Delete an existing character for an account. */
        deleteCharacter: builder.mutation<void, DeleteCharacterArgs>({
            query: ({ accountId, characterId }) => ({
                url: characterUrl({ accountId, characterId }),
                method: 'DELETE',
            }),
            invalidatesTags: (_, __, { accountId, characterId }) => [
                { type: 'Characters', id: accountId },
                { type: 'CharacterSkills', id: characterId },
            ],
        }),

        /**
         * Update a character skill preference (e.g. pin/unpin).
         *
         * Applies an optimistic cache update immediately so the UI reflects
         * the change without waiting for the server.  If the server rejects
         * the request or the request times out, the cache is reverted to its
         * previous state automatically.
         */
        patchSkill: builder.mutation<Skill, PatchSkillArgs>({
            query: ({ accountId, characterId, skillId, body }) => ({
                url: `${characterUrl({ accountId, characterId })}/skills/${encodeURIComponent(skillId)}`,
                method: 'PATCH',
                body,
            }),
            async onQueryStarted({ accountId, characterId, skillId, body }, { dispatch, queryFulfilled }) {
                await applyOptimistic(
                    dispatch(
                        sorremApi.util.updateQueryData(
                            'getCharacterSkills',
                            { accountId, characterId },
                            (draft) => {
                                // Nothing cached (never fetched, or the fetch failed): nothing to patch.
                                const skill = (draft as Skill[] | undefined)?.find((s) => s.id === skillId);
                                if (skill !== undefined && body.isPinned !== undefined) {
                                    skill.isPinned = body.isPinned;
                                }
                            },
                        ),
                    ),
                    queryFulfilled,
                );
            },
            // Patched in place above, so the cache is already right: refetch only when the change failed (#329).
            invalidatesTags: (_, error, { characterId }) => (error ? [{ type: 'CharacterSkills', id: characterId }] : []),
        }),

        /** Fetch the list of available crucible templates. */
        getCrucibleTemplates: builder.query<CrucibleTemplate[], void>({
            query: () => 'crucible-templates',
            transformResponse: (response: ApiResourceCollection<CrucibleTemplate>) => response.data,
        }),

        /** The sizes of random crucible players can create (#223), smallest first. */
        getCrucibleRecipes: builder.query<CrucibleRecipeSummary[], void>({
            query: () => 'crucible-recipes',
            transformResponse: (response: ApiResourceCollection<CrucibleRecipeSummary>) => response.data,
        }),

        /** A random crucible's name, backstory, place and saturation for the create form (#225); no seed rolls a new one. */
        getCrucibleFlavor: builder.query<CrucibleFlavorPreview, PreviewCrucibleFlavorArgs>({
            query: ({ recipeId, veilSaturation, flavorSeed }) => {
                const params = new URLSearchParams();
                if (veilSaturation) {
                    params.set('veilSaturation', veilSaturation);
                }
                if (flavorSeed) {
                    params.set('seed', flavorSeed);
                }
                const queryString = params.toString();
                return `crucible-recipes/${encodeURIComponent(recipeId)}/flavor${queryString ? `?${queryString}` : ''}`;
            },
        }),

        /** Create a new crucible from a template or with custom settings. */
        createCrucible: builder.mutation<CrucibleRecord, CreateCrucibleRequest>({
            query: (body) => ({
                url: 'crucibles',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['OpenCrucibles'],
        }),

        /** Whether an invite link's crucible can be joined (#216). */
        getCrucibleInviteStatus: builder.query<CrucibleInviteStatus, string>({
            query: (crucibleId) => `crucibles/${encodeURIComponent(crucibleId)}/invite`,
        }),

        /** The crucible an invite code names, and whether the character can join it (`already-member` when it's in). */
        getCrucibleInviteByCode: builder.query<CrucibleInviteStatus, { inviteCode: string; characterId: string | null }>({
            query: ({ inviteCode, characterId }) => `crucible-invites/${encodeURIComponent(inviteCode)}`
                + (characterId !== null ? `?characterId=${encodeURIComponent(characterId)}` : ''),
        }),

        /** Send a tester's bug report with its context (#218). */
        submitBugReport: builder.mutation<BugReportReceipt, BugReportSubmission>({
            query: (body) => ({
                url: 'telemetry/bug-reports',
                method: 'POST',
                body,
            }),
        }),

    }),
});

export const {
    useGetMapCatalogQuery,
    useGetLoreCatalogQuery,
    useGetLoreDocumentQuery,
    useGetNpcDefinitionsQuery,
    useGetProjectileCatalogQuery,
    useGetPropCatalogQuery,
    useGetNamedItemsQuery,
    useSaveNamedItemMutation,
    useGetItemDefinitionsQuery,
    useSaveItemDefinitionMutation,
    useGetItemModelsQuery,
    useGetItemModelSlotsQuery,
    useSaveItemModelMutation,
    useUploadItemModelFileMutation,
    useUploadItemIconMutation,
    useGetSoundLibraryQuery,
    useGetOpenCruciblesInfiniteQuery,
    useJoinCrucibleMutation,
    useLeaveCrucibleMutation,
    useGetAccountQuery,
    useGetAccountSettingsQuery,
    usePatchAccountSettingsMutation,
    useGetAuthSessionQuery,
    useGetCharactersQuery,
    useGetCharacterSkillsQuery,
    useGetCharacterCommissionsQuery,
    useGetUpcomingCommissionsQuery,
    useGetStartingEquipmentQuery,
    useAcceptCommissionMutation,
    useRespecCharacterAttributesMutation,
    useTradeCharacterItemMutation,
    useUpgradeCharacterItemMutation,
    useRerollCharacterItemAffixMutation,
    useRaiseEssenceSkillMutation,
    useRespecEssenceSkillsMutation,
    useMoveCharacterItemMutation,
    useGetCommissionQuery,
    useGetCutsceneQuery,
    useGetPendingCrucibleCutscenesQuery,
    useGetCrucibleModelManifestQuery,
    useMarkCutsceneSeenMutation,
    useGetPendingOfferCutscenesQuery,
    useMarkOfferCutsceneSeenMutation,
    useGetCharacterJournalQuery,
    useGetCharacterVisionsQuery,
    useGetCompendiumChaptersQuery,
    useCreateCompendiumChapterMutation,
    useUpdateCompendiumChapterMutation,
    useDeleteCompendiumChapterMutation,
    useSetCompendiumChapterVisionsMutation,
    useAddCompendiumChapterVisionMutation,
    useRemoveCompendiumChapterVisionMutation,
    useCreateCharacterMutation,
    useReorderCharactersMutation,
    usePutCharacterActionBarHotkeysMutation,
    useDeleteCharacterMutation,
    usePatchSkillMutation,
    useGetCrucibleTemplatesQuery,
    useGetCrucibleRecipesQuery,
    useCreateCrucibleMutation,
    useSubmitBugReportMutation,
    useGetCrucibleInviteStatusQuery,
    useGetCrucibleInviteByCodeQuery,
    useLazyGetCrucibleInviteByCodeQuery,
    useLazyGetCrucibleFlavorQuery,
} = sorremApi;
